import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChefHat, UserRound, Utensils, FileEdit, MessageCircle, Star, BarChart3, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LoadingState } from "../components/states";
import { ChefProfileForm } from "../components/kitchen/ChefProfileForm";
import { KitchenRecipes } from "../components/kitchen/KitchenRecipes";
import { KitchenInteractions } from "../components/kitchen/KitchenInteractions";
import { KitchenRatings } from "../components/kitchen/KitchenRatings";
import { KitchenAnalytics } from "../components/kitchen/KitchenAnalytics";

const TABS = [
  ["profile", "Chef Profile", UserRound], ["recipes", "My Recipes", Utensils], ["drafts", "Drafts", FileEdit],
  ["interactions", "Interactions", MessageCircle], ["ratings", "Ratings", Star], ["analytics", "Analytics", BarChart3],
];

export default function MyKitchen() {
  const { user, refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "recipes";
  const [data, setData] = useState(null);

  const load = useCallback(() => api.get("/kitchen").then(({ data }) => setData(data)).catch(() => setData(false)), []);
  useEffect(() => { window.scrollTo(0, 0); load(); }, [load]);

  const setTab = (t) => { const p = new URLSearchParams(params); p.set("tab", t); setParams(p); };

  if (data === null) return <LoadingState label="Opening your kitchen…" />;
  if (data === false) return <div className="py-16 text-center text-ink-soft">Could not load My Kitchen. Please refresh.</div>;

  if (!data.chef) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="my-kitchen-onboarding">
        <p className="inline-flex items-center gap-2 rounded-full bg-white border border-border px-4 py-1.5 text-sm font-bold text-ink-soft shadow-soft"><ChefHat size={15} className="text-coral" /> My Kitchen</p>
        <div className="mt-6">
          <ChefProfileForm chef={null} specialties={data.specialties} user={user} onSaved={async () => { await refreshUser(); load(); }} />
        </div>
        <div className="mt-6 grid sm:grid-cols-3 gap-3 text-sm">
          {[["Share original recipes", "With their history, ingredients and method — reviewed by our team before going live."], ["Build a following", "Members can follow you and get notified when you publish."], ["Hear from cooks", "Comments, suggestions and ratings land in your Interactions inbox."]].map(([t, d]) => (
            <div key={t} className="rounded-2xl bg-white border border-border p-4"><p className="font-bold text-ink">{t}</p><p className="text-ink-soft mt-1">{d}</p></div>
          ))}
        </div>
      </div>
    );
  }

  const { chef, stats, recipes, top_recipes } = data;
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10" data-testid="my-kitchen">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 min-w-0">
          <img src={chef.avatar} alt="" className="w-14 h-14 rounded-2xl object-cover border border-border shrink-0" />
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-widest text-coral">My Kitchen</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-black text-ink truncate">{chef.display_name}</h1>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`rounded-full px-3 py-1 text-xs font-bold ${chef.status === "LIVE" ? "bg-leaf/10 text-leaf" : "bg-secondary text-ink-soft"}`} data-testid="kitchen-status">{chef.status === "LIVE" ? "Profile live" : "Profile draft"}</span>
          <Link to={`/chefs/${chef.slug}`} data-testid="view-public-profile" className="inline-flex items-center gap-1.5 text-sm font-bold text-coral hover:underline">View public profile <ExternalLink size={14} /></Link>
        </div>
      </div>

      <div className="mt-6 flex gap-1.5 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="kitchen-tabs">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`kitchen-tab-${k}`}
            className={`shrink-0 inline-flex items-center gap-2 rounded-full px-4 h-11 text-sm font-bold transition-colors ${tab === k ? "bg-ink text-white" : "bg-white border border-border text-ink-soft hover:border-coral"}`}>
            <Icon size={15} /> {l}
            {k === "drafts" && stats.drafts > 0 && <span className={`text-xs rounded-full px-1.5 ${tab === k ? "bg-white/20" : "bg-secondary"}`}>{stats.drafts}</span>}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "profile" && <ChefProfileForm chef={chef} specialties={data.specialties} user={user} onSaved={load} />}
        {tab === "recipes" && <KitchenRecipes recipes={recipes} chefSlug={chef.slug} reload={load} />}
        {tab === "drafts" && <KitchenRecipes recipes={recipes} chefSlug={chef.slug} onlyDrafts reload={load} />}
        {tab === "interactions" && <KitchenInteractions />}
        {tab === "ratings" && <KitchenRatings chefSlug={chef.slug} />}
        {tab === "analytics" && <KitchenAnalytics stats={stats} topRecipes={top_recipes} chefSlug={chef.slug} />}
      </div>
    </div>
  );
}
